<template>
  <v-container fluid class="intelligence-profile pa-4">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-btn text color="#007AFF" @click="goBack">
            <v-icon left>mdi-arrow-left</v-icon>
            {{ backLabel }}
          </v-btn>
          <v-spacer />
          <v-btn outlined color="#8B5CF6" class="mr-2" @click="prepInterview">
            <v-icon left>mdi-head-question</v-icon>
            Interview Prep
          </v-btn>
          <v-btn outlined color="#F59E0B" class="mr-2" @click="analyzeGap">
            <v-icon left>mdi-chart-box</v-icon>
            Skills Gap
          </v-btn>
          <v-btn outlined color="#007AFF" class="mr-2" @click="findSimilar">
            <v-icon left>mdi-account-search</v-icon>
            Find Similar
          </v-btn>
          <v-btn color="primary" dark @click="toggleChat">
            <v-icon left>mdi-forum</v-icon>
            Deep Dive
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-alert v-if="hasError" type="error" dismissible class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <v-progress-linear v-if="isLoading" indeterminate color="#007AFF" class="mb-4" />

    <template v-if="hasProfile">
      <v-card outlined class="mb-4">
        <v-card-title class="py-3 px-4 profile-header-bg">
          <div class="flex-grow-1">
            <div class="text-h5 font-weight-bold white--text">
              {{ profile.name }}
            </div>
            <div class="text-subtitle-2 white--text" style="opacity: 0.8">
              {{ profile.currentTitle }} at {{ profile.currentCompany }}
            </div>
          </div>
          <div class="d-flex">
            <v-chip small color="#007AFF" text-color="white" class="mr-2" v-if="profile.seniorityLevel">
              {{ profile.seniorityLevel }}
            </v-chip>
            <v-chip small color="#8B5CF6" text-color="white" v-if="profile.specialistCluster">
              {{ profile.specialistCluster }}
            </v-chip>
          </div>
        </v-card-title>
      </v-card>

      <v-row>
        <v-col cols="12" md="8">
          <v-card outlined class="pa-4 mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              Top Skills
            </h3>
            <v-chip
              v-for="skill in profileSkills.slice(0, 15)"
              :key="skill.name || skill"
              small
              color="#E3F2FD"
              text-color="#007AFF"
              class="mr-2 mb-2"
            >
              {{ skill.name || skill }}
              <span v-if="skill.proficiency" class="ml-1 font-weight-bold">({{ skill.proficiency }})</span>
            </v-chip>
          </v-card>

          <v-card outlined class="pa-4 mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              Achievements by Job
            </h3>
            <v-expansion-panels accordion flat v-if="achievementsByJob.length > 0">
              <v-expansion-panel v-for="job in achievementsByJob" :key="job.jobTitle + job.company">
                <v-expansion-panel-header>
                  <div>
                    <span class="text-body-2 font-weight-bold">{{ job.jobTitle }}</span>
                    <span class="text-caption grey--text ml-2">at {{ job.company }}</span>
                    <v-chip x-small color="#F59E0B" text-color="white" class="ml-2">
                      {{ job.achievements ? job.achievements.length : 0 }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(ach, idx) in job.achievements" :key="idx" class="achievement-item mb-3">
                    <div v-if="ach.isStarFormat" class="star-achievement">
                      <div class="d-flex align-center mb-1">
                        <v-icon small color="#F59E0B" class="mr-1">mdi-trophy</v-icon>
                        <span class="text-body-2 font-weight-bold">{{ achievementSummary(ach) }}</span>
                        <v-chip x-small color="#F59E0B" text-color="white" class="ml-2">STAR</v-chip>
                      </div>
                      <div class="star-breakdown pl-6">
                        <div v-if="ach.situation" class="mb-1">
                          <span class="text-caption font-weight-bold" style="color: #007AFF">S: </span>
                          <span class="text-caption">{{ ach.situation }}</span>
                        </div>
                        <div v-if="ach.task" class="mb-1">
                          <span class="text-caption font-weight-bold" style="color: #10B981">T: </span>
                          <span class="text-caption">{{ ach.task }}</span>
                        </div>
                        <div v-if="ach.action" class="mb-1">
                          <span class="text-caption font-weight-bold" style="color: #F59E0B">A: </span>
                          <span class="text-caption">{{ ach.action }}</span>
                        </div>
                        <div v-if="ach.result" class="mb-1">
                          <span class="text-caption font-weight-bold" style="color: #8B5CF6">R: </span>
                          <span class="text-caption">{{ ach.result }}</span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="d-flex align-start">
                      <v-icon small color="#10B981" class="mr-2 mt-1">mdi-trophy</v-icon>
                      <span class="text-body-2" style="white-space: normal">{{ ach.text || ach.bullet || String(ach) }}</span>
                    </div>
                  </div>
                  <div v-if="!job.achievements || job.achievements.length === 0" class="text-center py-2 grey--text text-caption">
                    No achievements for this role
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <div v-else class="text-center py-4 grey--text">No achievements available</div>
          </v-card>

          <v-card outlined class="pa-4 mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              Small Wins
            </h3>
            <div v-if="smallWins.length > 0">
              <div class="mb-3">
                <div class="text-caption grey--text mb-2" v-if="validatedWins.length > 0">Validated</div>
                <div v-for="(win, idx) in validatedWins" :key="'v-' + idx" class="d-flex align-center mb-2">
                  <v-icon small color="#10B981" class="mr-2">mdi-check-circle</v-icon>
                  <span class="text-body-2 flex-grow-1">{{ win.text || win }}</span>
                  <v-chip x-small color="#10B981" text-color="white">Validated</v-chip>
                </div>
              </div>
              <div v-if="unvalidatedWins.length > 0">
                <div class="text-caption grey--text mb-2">Unvalidated</div>
                <div v-for="(win, idx) in unvalidatedWins" :key="'u-' + idx" class="d-flex align-center mb-2">
                  <v-icon small color="#9E9E9E" class="mr-2">mdi-circle-outline</v-icon>
                  <span class="text-body-2 flex-grow-1">{{ win.text || win }}</span>
                  <v-chip x-small color="#9E9E9E" text-color="white">Pending</v-chip>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-4 grey--text">No small wins recorded</div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              Work Timeline
            </h3>
            <v-timeline dense v-if="workHistory.length > 0">
              <v-timeline-item
                v-for="(job, idx) in workHistory"
                :key="idx"
                small
                :color="idx === 0 ? '#007AFF' : '#9E9E9E'"
              >
                <div class="text-body-2 font-weight-bold">{{ job.title || job.jobTitle }}</div>
                <div class="text-caption grey--text">{{ job.company }}</div>
                <div class="text-caption grey--text">{{ job.startDate }} — {{ job.endDate || 'Present' }}</div>
              </v-timeline-item>
            </v-timeline>
            <div v-else class="text-center py-4 grey--text">No timeline data</div>
          </v-card>

          <v-card outlined class="pa-4 mb-4" v-if="profile.education && profile.education.length > 0">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              Education
            </h3>
            <div v-for="(edu, idx) in profile.education" :key="idx" class="mb-2">
              <div class="text-body-2 font-weight-bold">{{ edu.degree && edu.field ? edu.degree + ' in ' + edu.field : (edu.degree || edu.field || '') }}</div>
              <div class="text-caption grey--text">{{ edu.institution || edu.school || '' }}</div>
            </div>
          </v-card>

          <v-card outlined class="pa-4 mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              Profile Stats
            </h3>
            <div class="d-flex justify-space-between mb-2">
              <span class="text-caption grey--text">Total Skills</span>
              <span class="text-body-2 font-weight-bold">{{ profile.totalSkills || profileSkills.length }}</span>
            </div>
            <div class="d-flex justify-space-between mb-2">
              <span class="text-caption grey--text">Achievements</span>
              <span class="text-body-2 font-weight-bold">{{ profile.totalAchievements || 0 }}</span>
            </div>
            <div class="d-flex justify-space-between mb-2">
              <span class="text-caption grey--text">Wins</span>
              <span class="text-body-2 font-weight-bold">{{ profile.totalWins || smallWins.length }}</span>
            </div>
            <div class="d-flex justify-space-between">
              <span class="text-caption grey--text">Validated</span>
              <span class="text-body-2 font-weight-bold" style="color: #10B981">{{ profile.totalWinsValidated || validatedWins.length }}</span>
            </div>
          </v-card>

          <v-card outlined class="pa-4 mb-4" v-if="isAdmin && (profile.consentSource || profile.consent_source)">
            <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
              <v-icon small class="mr-1 theme-heading">mdi-shield-check</v-icon>
              Data Governance
            </h3>
            <div class="d-flex justify-space-between mb-2">
              <span class="text-caption grey--text">Consent Source</span>
              <v-chip x-small :color="consentChipColor" text-color="white">
                {{ consentLabel }}
              </v-chip>
            </div>
            <div class="d-flex justify-space-between">
              <span class="text-caption grey--text">Consent Date</span>
              <span class="text-body-2">{{ consentDate }}</span>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-navigation-drawer
      v-model="isChatOpen"
      right
      temporary
      width="420"
      app
      class="profile-chat-drawer"
    >
      <div class="d-flex flex-column" style="height: 100%">
        <div class="pa-3 d-flex align-center profile-header-bg" style="min-height: 56px">
          <v-icon color="white" class="mr-2">mdi-forum</v-icon>
          <span class="white--text font-weight-bold flex-grow-1">Profile Deep Dive</span>
          <v-btn icon dark small @click="setChatOpen(false)">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="chatMessages.length === 0" class="pa-3">
          <div class="text-caption grey--text mb-3">
            Ask questions about {{ profile ? profile.name : 'this profile' }}'s career evidence, skills, and experience.
          </div>
          <v-chip
            v-for="(chip, idx) in chatSuggestions"
            :key="idx"
            small
            outlined
            color="#007AFF"
            class="mr-2 mb-2"
            style="cursor: pointer"
            @click="sendSuggestion(chip)"
          >
            {{ chip }}
          </v-chip>
        </div>

        <div class="flex-grow-1 overflow-y-auto pa-3" ref="chatScroll">
          <div
            v-for="(msg, idx) in chatMessages"
            :key="idx"
            class="mb-3"
          >
            <div v-if="msg.role === 'user'" class="d-flex justify-end">
              <div class="pa-2 px-3 rounded-lg" style="background: #007AFF; color: white; max-width: 85%; border-radius: 12px 12px 2px 12px">
                <span class="text-body-2">{{ msg.content }}</span>
              </div>
            </div>
            <div v-else-if="msg.role === 'assistant'" class="d-flex flex-column align-start">
              <div class="pa-2 px-3 rounded-lg chat-assistant-bubble" style="max-width: 85%; border-radius: 12px 12px 12px 2px">
                <div class="text-body-2 evidence-content" style="white-space: pre-wrap" v-html="formatEvidenceMessage(msg.content)"></div>
              </div>
              <div v-if="msg.suggestedQuestions && msg.suggestedQuestions.length > 0" class="mt-2" style="max-width: 85%">
                <v-chip
                  v-for="(sq, sqIdx) in msg.suggestedQuestions"
                  :key="'sq-' + idx + '-' + sqIdx"
                  x-small
                  outlined
                  color="#8B5CF6"
                  class="mr-1 mb-1"
                  style="cursor: pointer"
                  @click="sendSuggestion(sq)"
                >
                  {{ sq }}
                </v-chip>
              </div>
            </div>
            <div v-else class="d-flex justify-start">
              <div class="pa-2 px-3 rounded-lg" style="background: #FFF3F3; border: 1px solid #EF4444; max-width: 85%; border-radius: 12px">
                <v-icon x-small color="#EF4444" class="mr-1">mdi-alert</v-icon>
                <span class="text-body-2" style="color: #EF4444">{{ msg.content }}</span>
              </div>
            </div>
          </div>
          <div v-if="isChatLoading" class="d-flex justify-start mb-3">
            <div class="pa-2 px-3 rounded-lg chat-assistant-bubble" style="border-radius: 12px 12px 12px 2px">
              <v-progress-circular indeterminate size="16" width="2" color="#007AFF" class="mr-2" />
              <span class="text-caption grey--text">Analyzing profile...</span>
            </div>
          </div>
        </div>

        <div class="pa-3" style="border-top: 1px solid #e0e0e0">
          <div class="d-flex align-center">
            <v-text-field
              v-model="chatInput"
              placeholder="Ask about this profile..."
              dense
              outlined
              hide-details
              class="mr-2"
              @keyup.enter="handleSendChat"
              :disabled="isChatLoading"
            />
            <v-btn
              icon
              color="#007AFF"
              @click="handleSendChat"
              :disabled="!chatInput.trim() || isChatLoading"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
          <div class="d-flex justify-end mt-1" v-if="chatMessages.length > 0">
            <v-btn x-small text color="grey" @click="clearChat">
              <v-icon x-small left>mdi-broom</v-icon>
              Clear
            </v-btn>
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

export default {
  name: "IntelligenceProfile",
  data() {
    return {
      chatInput: "",
      chatSuggestions: [
        "Tell me about a time they led a team through a challenge",
        "What evidence shows their problem-solving ability?",
        "Describe a measurable impact they delivered",
        "What STAR achievements demonstrate leadership?",
        "How have they handled conflict or difficult situations?",
        "What results have they driven in their career?",
        "Show me evidence of cross-functional collaboration",
        "What achievements demonstrate innovation?",
      ],
    };
  },
  computed: {
    ...mapState("intelligenceProfile", [
      "profile",
      "achievementsByJob",
      "isLoading",
      "hasError",
      "errorMessage",
      "chatMessages",
      "isChatLoading",
      "isChatOpen",
    ]),
    ...mapGetters("intelligenceProfile", ["hasProfile", "profileSkills"]),
    isAdmin() {
      return this.$store.state.auth.companyAdmin;
    },
    consentLabel() {
      const src = this.profile?.consentSource || this.profile?.consent_source || '';
      const labels = { employment_import: 'Employment Import', explicit_consent: 'Explicit Consent', self_registered: 'Self-Registered' };
      return labels[src] || src || 'Unknown';
    },
    consentChipColor() {
      const src = this.profile?.consentSource || this.profile?.consent_source || '';
      if (src === 'explicit_consent') return '#10B981';
      if (src === 'employment_import') return '#007AFF';
      return '#9E9E9E';
    },
    consentDate() {
      const ts = this.profile?.consentTimestamp || this.profile?.consent_timestamp;
      if (!ts) return 'Not recorded';
      try { return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch (e) { return ts; }
    },
    smallWins() {
      return (this.profile && this.profile.smallWins) || [];
    },
    validatedWins() {
      if (this.profile && this.profile.winsValidated && this.profile.winsValidated.length > 0) {
        return this.profile.winsValidated;
      }
      return this.smallWins.filter(w => w.validated || w.isValidated);
    },
    unvalidatedWins() {
      if (this.profile && this.profile.winsUnvalidated && this.profile.winsUnvalidated.length > 0) {
        return this.profile.winsUnvalidated;
      }
      return this.smallWins.filter(w => !w.validated && !w.isValidated);
    },
    workHistory() {
      if (!this.profile) return [];
      return this.profile.workHistory || this.profile.workTimeline || [];
    },
    backLabel() {
      const from = this.$route.query.from;
      if (from === 'search') return 'Back to Search';
      if (from === 'jdmatch') return 'Back to JD Match';
      if (from === 'succession') return 'Back to Succession';
      if (from === 'sagehr') return 'Back to Sage HR';
      if (from === 'directory') return 'Back to Directory';
      if (from === 'orgchart') return 'Back to Org Chart';
      if (from === 'teambuilder') return 'Back to Team Builder';
      if (from === 'insights') return 'Back to Insights';
      if (from === 'findsimilar') return 'Back to Similar';
      return 'Back';
    },
  },
  watch: {
    "$route.params.profileId"(newId) {
      if (newId) {
        this.loadProfile(newId);
      }
    },
  },
  mounted() {
    const profileId = this.$route.params.profileId;
    if (profileId) {
      this.loadProfile(profileId);
    }
  },
  methods: {
    ...mapActions("intelligenceProfile", ["loadProfile", "loadSimilar", "sendChatMessage"]),
    ...mapMutations("intelligenceProfile", ["toggleChat", "setChatOpen", "clearChat"]),
    goBack() {
      const from = this.$route.query.from;
      if (from === 'search') {
        this.$router.push({ name: 'IntelligenceFindTalent', query: { tab: 'search' } });
      } else if (from === 'jdmatch') {
        this.$router.push({ name: 'IntelligenceFindTalent', query: { tab: 'jdmatch' } });
      } else if (from === 'succession') {
        this.$router.push({ name: 'IntelligenceFindTalent', query: { tab: 'succession' } });
      } else if (from === 'sagehr') {
        this.$router.push({ name: 'IntelligenceSageHR' });
      } else if (from === 'directory') {
        this.$router.push({ name: 'IntelligencePeople', query: { tab: 'directory' } });
      } else if (from === 'orgchart') {
        this.$router.push({ name: 'IntelligencePeople', query: { tab: 'orgchart' } });
      } else if (from === 'teambuilder') {
        this.$router.push({ name: 'IntelligenceTeamBuilder' });
      } else if (from === 'insights') {
        this.$router.push({ name: 'IntelligenceInsightsHub', query: { tab: 'intelligence' } });
      } else if (from === 'findsimilar') {
        this.$router.go(-1);
      } else if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: 'IntelligenceFindTalent', query: { tab: 'search' } });
      }
    },
    findSimilar() {
      const profileId = this.$route.params.profileId;
      this.$router.push({ name: "IntelligenceFindSimilar", params: { profileId } });
    },
    prepInterview() {
      const profileId = this.$route.params.profileId;
      this.$router.push({ name: "IntelligenceInterviewPrep", query: { profileId } });
    },
    analyzeGap() {
      const profileId = this.$route.params.profileId;
      this.$router.push({ name: "IntelligenceSkillsGap", query: { profileId } });
    },
    achievementSummary(ach) {
      if (ach.bullet) return ach.bullet;
      if (ach.action) return ach.action;
      if (ach.result) return ach.result;
      return "Achievement";
    },
    async handleSendChat() {
      const msg = this.chatInput.trim();
      if (!msg) return;
      this.chatInput = "";
      await this.sendChatMessage(msg);
      this.$nextTick(() => {
        const el = this.$refs.chatScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    async sendSuggestion(text) {
      this.chatInput = "";
      await this.sendChatMessage(text);
      this.$nextTick(() => {
        const el = this.$refs.chatScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    formatEvidenceMessage(content) {
      if (!content) return "";
      let formatted = content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      formatted = formatted.replace(
        /\b(Situation|Task|Action|Result):/g,
        '<span class="evidence-star-label">$1:</span>'
      );
      formatted = formatted.replace(
        /\[Evidence:([^\]]+)\]/g,
        '<span class="evidence-citation">[Evidence:$1]</span>'
      );
      formatted = formatted.replace(
        /\[STAR:([^\]]+)\]/g,
        '<span class="evidence-star-ref">[STAR:$1]</span>'
      );
      return formatted;
    },
  },
};
</script>

<style scoped>
.intelligence-profile {
  max-width: 1200px;
}
.achievement-item {
  border-bottom: 1px solid #f5f5f5;
  padding-bottom: 8px;
}
.achievement-item:last-child {
  border-bottom: none;
}
.star-breakdown {
  background: #FAFAFA;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 4px;
}
.profile-header-bg {
  background: #1E3A5F;
}
.chat-assistant-bubble {
  background: #f5f5f5;
}
.evidence-content .evidence-star-label {
  font-weight: 700;
  color: #F59E0B;
}
.evidence-content .evidence-citation {
  background: #EDE9FE;
  color: #7C3AED;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}
.evidence-content .evidence-star-ref {
  background: #FEF3C7;
  color: #D97706;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}
</style>

<style>
.profile-chat-drawer .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.theme--dark .profile-header-bg {
  background: #2a2a3e;
}
.theme--dark .star-breakdown {
  background: #2a2a3e;
}
.theme--dark .chat-assistant-bubble {
  background: #2a2a3e;
}
</style>
